import React, { Suspense, lazy } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { InstallPWA } from "./components/InstallPWA";
import { AffiliateTracker } from "./components/AffiliateTracker";
import "./App.css";
import "./lib/i18n";
import { AuthProvider } from "./lib/auth-context";
import { SiteSettingsProvider } from "./lib/site-settings-context";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { RedirectGate } from "./components/RedirectGate";
import { LoadingDots } from "./components/LoadingDots";

const Home = lazy(() => import("./pages/Home"));
const Browse = lazy(() => import("./pages/Browse"));
const ListingDetail = lazy(() => import("./pages/ListingDetail"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const CreateListing = lazy(() => import("./pages/CreateListing"));
const Profile = lazy(() => import("./pages/Profile"));
const MyListings = lazy(() => import("./pages/MyListings"));
const EditListing = lazy(() => import("./pages/EditListing"));
const Favorites = lazy(() => import("./pages/Favorites"));
const Admin = lazy(() => import("./pages/Admin"));
const Premium = lazy(() => import("./pages/Premium"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Chat = lazy(() => import("./pages/Chat"));
const Chats = lazy(() => import("./pages/Chats"));
const Verification = lazy(() => import("./pages/Verification"));
const Support = lazy(() => import("./pages/Support"));
const Impressum = lazy(() => import("./pages/Impressum"));
const Datenschutz = lazy(() => import("./pages/Datenschutz"));
const AGB = lazy(() => import("./pages/AGB"));
const Widerruf = lazy(() => import("./pages/Widerruf"));
const Kontakt = lazy(() => import("./pages/Kontakt"));
const Settings = lazy(() => import("./pages/Settings"));
const Wantlist = lazy(() => import("./pages/Wantlist"));
const PublicWantlist = lazy(() => import("./pages/PublicWantlist"));
const EmailVerifyPending = lazy(() => import("./pages/EmailVerifyPending"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const SavedSearches = lazy(() => import("./pages/SavedSearches"));
const SellerStats = lazy(() => import("./pages/SellerStats"));
const Drafts = lazy(() => import("./pages/Drafts"));
const MapView = lazy(() => import("./pages/MapView"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PremiumSuccess = lazy(() => import("./pages/PremiumSuccess"));
const ShopsList = lazy(() => import("./pages/ShopsList"));
const BuyCredits = lazy(() => import("./pages/BuyCredits"));
const CreditsSuccess = lazy(() => import("./pages/CreditsSuccess"));
const Referrals = lazy(() => import("./pages/Referrals"));
const Following = lazy(() => import("./pages/Following"));
const BulkImport = lazy(() => import("./pages/BulkImport"));
const Affiliate = lazy(() => import("./pages/Affiliate"));
const ShopBySlug = lazy(() => import("./pages/ShopBySlug"));
const CmsPage = lazy(() => import("./pages/CmsPage"));

const Fallback = () => (
  <div className="min-h-[60vh] grid place-items-center text-[var(--text-3)]"><LoadingDots /></div>
);

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <SiteSettingsProvider>
          <BrowserRouter>
          <AffiliateTracker />
          <RedirectGate>
          <Suspense fallback={<Fallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/browse" element={<Browse />} />
              <Route path="/wantlist" element={<PublicWantlist />} />
              <Route path="/listing/:id" element={<ListingDetail />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/auth/check-email" element={<EmailVerifyPending />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/u/:username" element={<Profile />} />
              <Route path="/premium" element={<Premium />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/impressum" element={<Impressum />} />
              <Route path="/datenschutz" element={<Datenschutz />} />
              <Route path="/agb" element={<AGB />} />
              <Route path="/widerruf" element={<Widerruf />} />
              <Route path="/kontakt" element={<Kontakt />} />
              <Route path="/premium/success" element={<PremiumSuccess />} />
              <Route path="/shops" element={<ShopsList />} />
              <Route
                path="/credits"
                element={<ProtectedRoute><BuyCredits /></ProtectedRoute>}
              />
              <Route
                path="/credits/success"
                element={<ProtectedRoute><CreditsSuccess /></ProtectedRoute>}
              />
              <Route
                path="/chats"
                element={<ProtectedRoute><Chats /></ProtectedRoute>}
              />
              <Route
                path="/chats/:id"
                element={<ProtectedRoute><Chat /></ProtectedRoute>}
              />
              <Route
                path="/verification"
                element={<ProtectedRoute><Verification /></ProtectedRoute>}
              />
              <Route
                path="/support"
                element={<ProtectedRoute><Support /></ProtectedRoute>}
              />
              <Route
                path="/sell"
                element={<ProtectedRoute><CreateListing /></ProtectedRoute>}
              />
              <Route
                path="/me/listings"
                element={<ProtectedRoute><MyListings /></ProtectedRoute>}
              />
              <Route
                path="/me/listings/:id/edit"
                element={<ProtectedRoute><EditListing /></ProtectedRoute>}
              />
              <Route
                path="/me/favorites"
                element={<ProtectedRoute><Favorites /></ProtectedRoute>}
              />
              <Route
                path="/me/settings"
                element={<ProtectedRoute><Settings /></ProtectedRoute>}
              />
              <Route
                path="/me/wantlist"
                element={<ProtectedRoute><Wantlist /></ProtectedRoute>}
              />
              <Route
                path="/me/searches"
                element={<ProtectedRoute><SavedSearches /></ProtectedRoute>}
              />
              <Route
                path="/me/stats"
                element={<ProtectedRoute><SellerStats /></ProtectedRoute>}
              />
              <Route
                path="/me/drafts"
                element={<ProtectedRoute><Drafts /></ProtectedRoute>}
              />
              <Route
                path="/me/referrals"
                element={<ProtectedRoute><Referrals /></ProtectedRoute>}
              />
              <Route
                path="/me/following"
                element={<ProtectedRoute><Following /></ProtectedRoute>}
              />
              <Route
                path="/sell/bulk"
                element={<ProtectedRoute><BulkImport /></ProtectedRoute>}
              />
              <Route
                path="/affiliate"
                element={<ProtectedRoute><Affiliate /></ProtectedRoute>}
              />
              <Route path="/map" element={<MapView />}/>
              <Route
                path="/admin"
                element={<ProtectedRoute requireAdmin><Admin /></ProtectedRoute>}
              />
              {/* Custom CMS Page (z.B. /ueber-uns) — fängt /:slug ab,
                  fällt aber bei nicht-existentem Slug auf 404 zurück.
                  Muss VOR der Shop-Slug Route stehen, damit CMS Vorrang hat. */}
              <Route path="/p/:slug" element={<CmsPage />} />
              {/* Shop-URL per Slug (nur Profi-Premium) — MUSS letzte konkrete Route sein */}
              <Route path="/:slug" element={<ShopBySlug />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          </RedirectGate>
        </BrowserRouter>
        <Toaster
          position="top-right"
          theme="dark"
          toastOptions={{
            style: { background: "var(--surface-1)", border: "1px solid var(--surface-3)", color: "var(--text-1)" },
          }}
          offset={80}
        />
        <InstallPWA />
        </SiteSettingsProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
